<template>
  <div>
    <teleport to="body">
      <div
        v-if="showDialog"
        ref="modal-backdrop"
        class="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black bg-opacity-50 p-4"
      >
        <div
          class="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          role="dialog"
          ref="modal"
          aria-modal="true"
          aria-labelledby="modal-headline"
        >
          <!-- Header -->
          <div class="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h1 class="text-3xl font-black text-blue tracking-tight">Scap.</h1>
              <h2 class="text-lg font-semibold text-gray-600 mt-1">{{ $t('language.title') }}</h2>
            </div>
            <button @click="goBack" class="p-2 hover:bg-gray-100 rounded-full transition-colors" :aria-label="$t('common.close')">
              <i class="fas fa-times text-gray-500 text-xl"></i>
            </button>
          </div>

          <!-- Content -->
          <div class="p-6 space-y-6">
            <!-- City Section -->
            <div class="space-y-3">
              <h3 class="font-bold text-lg text-gray-900">{{ $t('language.city') }}</h3>
              <p class="text-sm text-gray-500 leading-relaxed">
                {{ $t('language.cityIntro') }}
              </p>
              <div class="flex flex-wrap gap-2 -m-1">
                <label v-for="cityOption in cities" :key="cityOption" class="flex-1 min-w-[120px] flex items-center p-3 rounded-xl border-2 border-gray-200 hover:border-blue transition-colors cursor-pointer group">
                  <input
                    type="radio"
                    :id="cityOption"
                    :value="cityOption"
                    v-model="city"
                    class="sr-only peer"
                  >
                  <span class="w-4 h-4 border-2 border-gray-300 rounded-full peer-checked:border-blue peer-checked:bg-blue peer-focus:ring-2 peer-focus:ring-blue mr-3 flex-shrink-0 transition-all duration-200 group-hover:scale-105"></span>
                  <span class="text-sm font-medium text-gray-700 peer-checked:text-blue transition-colors">{{ cityOption }}</span>
                </label>
              </div>
            </div>

            <!-- Language Section -->
            <div class="space-y-3">
              <h3 class="font-bold text-lg text-gray-900">{{ $t('language.languageLabel') }}</h3>
              <div id="google_translate_element" class="hidden"></div>
              <select
                v-model="selectedLanguage"
                @change="changeLanguage"
                class="w-full p-3 rounded-xl border-2 border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue focus:border-blue text-gray-700 bg-white text-sm transition-all duration-200 hover:border-gray-300"
              >
                <option value="fr">Français</option>
                <option value="en">English</option>
                <option value="ar">العربية</option>
                <option value="es">Español</option>
              </select>
            </div>
          </div>

          <!-- Footer -->
          <div class="px-6 pb-6">
            <button
              @click="submit"
              class="w-full bg-blue hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              {{ $t('language.confirm') }}
            </button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import { SUPPORTED_CITIES } from '@/constants/cities';
import i18n, { setLocale } from '@/i18n';

export default {
  name: 'LanguageDialog',
  emits: ['dialog-closed', 'city-selected'],

  data() {
    return {
      city: '',
      showDialog: true,
      // Initialize from the active i18n locale, not a hard-coded 'fr',
      // so reopening the dialog reflects the user's current choice.
      selectedLanguage: i18n.global.locale.value,
      googleTranslateLoaded: false,
      cities: SUPPORTED_CITIES
    };
  },

  mounted() {
    this.loadGoogleTranslate();
  },

  methods: {
    submit() {
      this.showDialog = false;
      this.$emit('dialog-closed');
      this.$emit('city-selected', this.city);
    },
    goBack() {
      this.showDialog = false;
      this.$emit('dialog-closed');
    },
    loadGoogleTranslate() {
      if (!window.googleTranslateElementInit) {
        window.googleTranslateElementInit = () => {
          this.googleTranslateLoaded = true;
          new window.google.translate.TranslateElement(
            {
              pageLanguage: 'en',
              includedLanguages: 'en,fr,ar',
              layout: window.google.translate.TranslateElement.InlineLayout.HORIZONTAL,
              autoDisplay: false,
            },
            'google_translate_element'
          );

          // Hide Google Translate UI after it loads
          const style = document.createElement('style');
          style.innerHTML = `
            .goog-te-banner-frame.skiptranslate, 
            .goog-te-gadget-icon,
            .goog-te-gadget,
            iframe.goog-te-banner-frame,
            #goog-gt-tt,
            .goog-te-balloon-frame,
            .goog-text-highlight {
              display: none !important;
            }
            body {
              top: 0px !important;
            }
          `;
          document.head.appendChild(style);

          // Force hide Google Translate bar every 500ms
          setInterval(() => {
            const googFrame = document.querySelector('iframe.goog-te-banner-frame');
            if (googFrame) {
              googFrame.style.display = 'none';
            }
            const body = document.querySelector('body');
            if (body) {
              body.style.top = '0px';
            }
          }, 500);
        };

        const script = document.createElement('script');
        script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
        script.async = true;
        document.head.appendChild(script);
      }
    },
async changeLanguage() {
  const lang = this.selectedLanguage;

  // Single source of truth: setLocale updates vue-i18n + persists to
  // localStorage + flips <html lang> and dir (rtl for ar).
  setLocale(lang);

  // Keep Google Translate in sync during the migration period — it still
  // handles untranslated chunks of the site. Once vue-i18n coverage is
  // complete, drop the widget (and this call).
  if (this.$gt && typeof this.$gt.setLanguage === 'function') {
    await this.$gt.setLanguage(lang);
  }
}


  },
};
</script>

<style scoped>
/* Hide the Google Translate elements */
#google_translate_element {
  display: none;
}

.goog-te-banner-frame,
.goog-te-gadget-icon,
.goog-te-gadget,
#goog-gt-tt,
.goog-te-balloon-frame,
.goog-text-highlight,
iframe.goog-te-banner-frame {
  display: none !important;
}

body {
  top: 0px !important;
}

/* Custom radio button styles */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>